import { forwardRef, useCallback, useState } from "react"
import type { FC, ForwardedRef, ReactElement, Ref } from "react"

import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HUICombobox,
} from "@headlessui/react"
import type { ComboboxOptionsProps, ComboboxProps as HUIComboboxProps } from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { matchSorter } from "match-sorter"
import { useMeasure } from "react-use"
import { twMerge } from "tailwind-merge"

export type Option = {
  label: string
  value: string | number
  disabled?: boolean
  alwaysShowAt?: "start" | "end"
}

export type OptionFilter<T> = (list: Array<T>, q: string) => Array<T>

export type ComboboxProps<T extends Option> = HUIComboboxProps<T, boolean> & {
  options: Array<T>
  filterFn?: OptionFilter<T>
  menuAnchor?: ComboboxOptionsProps["anchor"]
  optionComponent?: FC<T>
  onChange?: (o: T) => void
  onQueryChange?: (q: string) => void
  placeholder?: string
  defaultValue?: T
}

export function defaultFilterFn<T extends Option>(list: Array<T>, q: string) {
  const groupedOptions = Object.groupBy(list, (o) => o.alwaysShowAt ?? "middle")
  const startOptions: T[] = groupedOptions.start ?? []
  const endOptions: T[] = groupedOptions.end ?? []

  let filteredOptions: T[] = groupedOptions.middle ?? []

  if (q) {
    filteredOptions = matchSorter(filteredOptions, q, { keys: ["label"] })
  }

  return [...startOptions, ...filteredOptions, ...endOptions]
}

export function DefaultOptionComponent<T extends Option>(props: T) {
  return <span className="block truncate group-data-selected:font-semibold">{props.label}</span>
}

function ComboboxFn<T extends Option>(
  {
    defaultValue,
    filterFn = defaultFilterFn,
    immediate = true,
    menuAnchor = "bottom",
    name,
    onChange,
    onQueryChange,
    optionComponent: OptionComponent = DefaultOptionComponent<T>,
    options,
    placeholder,
    value,
  }: ComboboxProps<T>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const [query, setQuery] = useState("")
  const filteredOptions = filterFn(options, query)
  const optionalProps = {
    ...(name && { name }),
    ...(defaultValue && { defaultValue }),
  }
  const [useMeasureRef, { width: boxWidth }] = useMeasure<HTMLElement>()
  const setQueryWithCallback = useCallback(
    (q: string) => {
      setQuery(q)
      onQueryChange?.(q)
    },
    [onQueryChange, setQuery]
  )

  return (
    <HUICombobox
      ref={useMeasureRef}
      value={value}
      immediate={immediate}
      onChange={(o: T) => {
        setQueryWithCallback("")
        onChange?.(o)
      }}
      onClose={() => setQuery("")}
      {...optionalProps}
    >
      <div className="relative">
        <ComboboxInput
          ref={ref}
          placeholder={placeholder}
          className="block w-full rounded-md border-none bg-white py-1.5 pr-12 pl-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:ring-0 focus:ring-offset-0 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-gray-400 sm:text-sm/6"
          onChange={(event) => setQueryWithCallback(event.target.value)}
          onBlur={() => setQueryWithCallback("")}
          displayValue={(o: T) => o?.label}
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md border-none px-2 shadow-none focus:outline-none">
          <ChevronDownIcon className="size-4 text-gray-400" aria-hidden="true" />
        </ComboboxButton>

        {filteredOptions.length > 0 && (
          <ComboboxOptions anchor={menuAnchor} className="tailwind-scope">
            <div
              className={twMerge(
                "mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 text-base ring-1 ring-black/5 drop-shadow-sm focus:outline-none sm:text-sm",
                menuAnchor ? "border" : "absolute z-10 w-full"
              )}
              style={menuAnchor ? { width: boxWidth } : {}}
            >
              {filteredOptions.map((o: T) => (
                <ComboboxOption
                  disabled={!!o.disabled}
                  key={o.value}
                  value={o}
                  className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-gray-100 data-focus:outline-none"
                >
                  <OptionComponent {...o} />
                </ComboboxOption>
              ))}
            </div>
          </ComboboxOptions>
        )}
      </div>
    </HUICombobox>
  )
}

export const Combobox = forwardRef(ComboboxFn) as <T extends Option>(
  p: ComboboxProps<T> & { ref?: Ref<HTMLDivElement> }
) => ReactElement
